export type PostHogPrimitive = string | number | boolean;

export type PostHogEventPayload = Readonly<Record<string, PostHogPrimitive | undefined>>;

type MarketingBasePayload = {
  readonly buyer_segment?: string;
  readonly cta_location?: string;
  readonly intended_path?: string;
  readonly locale?: string;
  readonly route_path?: string;
  readonly service_path?: string;
  readonly source_cta?: string;
  readonly source_path?: string;
};

type PageNotFoundPayload = {
  readonly locale: string;
  readonly referrer_bucket: "external" | "internal" | "none";
  readonly route_path: "/missing" | "/missing/missing" | "/missing/deep";
  readonly sampling_tier: "bot_probe" | "standard";
};

type ContactPayload = MarketingBasePayload & {
  readonly contact_method?: "book_call" | "email" | "whatsapp" | "phone" | string;
  readonly contact_tier?: "primary" | "secondary" | string;
};

type ScopeStage = 0 | 1 | 2 | 3 | 4;

type ScopeConfidencePayload = MarketingBasePayload & {
  readonly audience?: string;
  readonly previous_stage?: ScopeStage;
  readonly stage: ScopeStage;
  readonly source?: string;
};

type FitBand = "starter" | "growth" | "enterprise" | string;

type FitQuizPayload = MarketingBasePayload & {
  readonly audience?: string;
  readonly fit_band?: FitBand;
  readonly quiz_step?: 1 | 2 | 3;
  readonly quiz_answer_id?: string;
};

type RoiCalculatorPayload = MarketingBasePayload & {
  readonly aov_band?: string;
  readonly conversion_rate_band?: string;
  readonly projected_revenue_band?: string;
  readonly projection_months?: number;
  readonly traffic_band?: string;
};

type PartnerBadgePayload = MarketingBasePayload & {
  readonly partner_visibility?: "public" | "anonymous" | "sales_only" | string;
};

type FunnelPlannerPayload = MarketingBasePayload & {
  readonly template_id?: string;
};

type FunnelPlannerScenarioPayload = FunnelPlannerPayload & {
  readonly scenario_kind?: "baseline" | "conservative" | "target" | "optimistic" | "custom" | string;
};

type FunnelPlannerContextMenuPayload = FunnelPlannerPayload & {
  readonly action?: "open" | "add_stage" | "add_source" | "add_branch" | "add_retargeting" | "add_wait" | "add_survey" | "edit_node" | "edit_edge" | "delete_node" | "delete_edge" | "copy_stage_link" | string;
  readonly node_kind?: "stage" | "traffic_source" | "branch" | "retargeting" | "wait" | "survey" | string;
  readonly edge_kind?: "main" | "yes" | "no" | "retargeting" | "wait" | "survey" | "optional" | string;
};

type FunnelPlannerRecommendationPayload = FunnelPlannerPayload & {
  readonly recommendation_source?: "benchmark_default" | "user_input" | "calculated" | "estimated" | string;
  readonly recommendation_severity?: "info" | "warning" | "critical" | string;
};

type FunnelPlannerBlueprintPayload = FunnelPlannerPayload & {
  readonly blueprint_id?: string;
  readonly blueprint_category?: string;
};

type FunnelPlannerExportPayload = FunnelPlannerPayload & {
  readonly export_format?: "json" | "csv" | string;
};

type FunnelPlannerModelHealthPayload = FunnelPlannerPayload & {
  readonly warning_categories?: readonly string[];
  readonly warning_count?: number;
};

type FunnelPlannerFinalFieldCoveragePayload = FunnelPlannerPayload & {
  readonly planner_tracking_schema_version?: string;
  readonly planner_state_version?: number;
  readonly crm_snapshot_schema_version?: string;
  readonly final_field_count?: number;
  readonly field_group_count?: number;
  readonly traffic_channel_count?: number;
  readonly product_count?: number;
  readonly stage_count?: number;
  readonly graph_node_count?: number;
  readonly graph_edge_count?: number;
  readonly expense_count?: number;
  readonly scenario_count?: number;
  readonly experiment_count?: number;
  readonly actual_row_count?: number;
  readonly asset_count?: number;
  readonly tracking_configured_count?: number;
  readonly has_graph?: boolean;
  readonly has_actuals?: boolean;
  readonly has_expenses?: boolean;
  readonly has_assets?: boolean;
  readonly has_capacity_cap?: boolean;
  readonly has_custom_scenarios?: boolean;
  readonly has_experiments?: boolean;
  readonly has_reinvestment?: boolean;
  readonly has_model_health_warnings?: boolean;
  readonly model_health_warning_count?: number;
  readonly model_health_warning_category_count?: number;
  readonly visitor_band?: string;
  readonly customer_band?: string;
  readonly monthly_revenue_band?: string;
  readonly monthly_expense_band?: string;
};

type ExperienceEngagementPayload = MarketingBasePayload & {
  readonly campaign_key?: string;
  readonly budget_stage_bucket?: string;
  readonly component_family?: string;
  readonly component_id?: string;
  readonly content_theme?: string;
  readonly cta_id?: string;
  readonly device_shell?: string;
  readonly experience_id?: string;
  readonly experiment_key?: string;
  readonly experiment_variant?: string;
  readonly global_holdout?: boolean;
  readonly holdout_key?: "seoh-experience-global-holdout" | string;
  readonly locale_direction?: "ltr" | "rtl" | string;
  readonly marketing_compliance_gate?: "unknown" | "pending" | "passed" | "blocked" | "not_applicable" | string;
  readonly motion_mode?: string;
  readonly persona?: string;
  readonly route_template_id?: string;
  readonly shell_detection_state?: "detected" | "defaulted" | "unknown" | "not_applicable" | string;
  readonly recommended_path?: string;
  readonly recommended_service_line?: string;
  readonly result_tier?: string;
  readonly service_line?: string;
  readonly source_locale?: string;
  readonly taxonomy_version?: string;
  readonly tool_key?: string;
  readonly traffic_band?: string;
  readonly aov_band?: string;
  readonly conversion_rate_band?: string;
  readonly projected_revenue_band?: string;
  readonly variant_key?: string;
  readonly vertical_18plus?: boolean;
  readonly vertical_18plus_reason?: string;
  readonly xp_depth?: number | string;
  readonly xp_zone?: string;
};

type WhatsAppAggregateAnalyticsPayload = {
  readonly aggregation_schema?: "whatsapp_aggregate_v1";
  readonly window_from?: string;
  readonly window_to?: string;
  readonly date?: string;
  readonly inbound?: number;
  readonly outbound?: number;
  readonly sent?: number;
  readonly delivered?: number;
  readonly read?: number;
  readonly failed?: number;
  readonly reply_rate_percent?: number;
  readonly flow_version_id?: string;
  readonly node_id?: string;
  readonly action_type?: string;
  readonly attempts?: number;
  readonly successful?: number;
  readonly blocked?: number;
  readonly drop_off_percent?: number;
  readonly campaign_id?: string;
  readonly total_jobs?: number;
  readonly scheduled?: number;
  readonly completed?: number;
  readonly cancelled?: number;
  readonly completion_rate_percent?: number;
};

type RetentionCommonPayload = MarketingBasePayload & {
  readonly app_surface?: "client_portal" | "review_demo" | "mobile_app" | "pwa" | string;
  readonly display_mode?: string;
  readonly event_transport?: "browser_consent_gated" | "server_gated" | "worker_gated" | string;
  readonly health_bucket?: "unknown" | "green" | "yellow" | "red" | "not_enough_data" | string;
  readonly portal_state?: "locked" | "empty" | "error" | "ready" | "signed_out" | string;
  readonly retention_stage?:
    | "unknown"
    | "invite"
    | "onboarding"
    | "active"
    | "adopting"
    | "at_risk"
    | "save"
    | "paused"
    | "churned"
    | "winback"
    | "advocacy"
    | string;
  readonly retention_policy_version?: string;
  readonly route_template_id?: string;
  readonly signal_count_bucket?: "0" | "1" | "2_3" | "4_plus" | string;
  readonly source_event_kind?: string;
  readonly source_locale?: string;
  readonly source_template?: string;
  readonly taxonomy_version?: string;
};

type PortalActivationPayload = RetentionCommonPayload & {
  readonly activation_step?:
    | "portal_viewed"
    | "magic_link_requested"
    | "magic_link_sent"
    | "signed_in"
    | "dashboard_ready"
    | "dashboard_empty"
    | "first_status_viewed"
    | "first_document_opened"
    | "first_update_viewed"
    | "first_intake_viewed"
    | string;
};

type PortalAdoptionPayload = RetentionCommonPayload & {
  readonly adoption_signal?:
    | "dashboard_viewed"
    | "status_viewed"
    | "update_viewed"
    | "document_available"
    | "document_opened"
    | "intake_viewed"
    | "activation_checklist_viewed"
    | "first_value_path_opened"
    | "routine_cadence_opened"
    | "stored_value_opened"
    | "repeat_visit"
    | "power_path_candidate"
    | string;
};

type RetentionFeedbackPayload = RetentionCommonPayload & {
  readonly feedback_signal?:
    | "support_opened"
    | "feedback_loop_started"
    | "nps_prompt_viewed"
    | "nps_submitted"
    | "cancellation_reason_prompted"
    | "churn_interview_requested"
    | "praise_candidate"
    | "referral_candidate"
    | string;
  readonly feedback_stage?: "prompted" | "submitted" | "triaged" | "closed_loop" | "not_applicable" | string;
  readonly sentiment_bucket?: "unknown" | "promoter" | "passive" | "detractor" | "positive" | "neutral" | "negative" | string;
};

type RetentionRiskPayload = RetentionCommonPayload & {
  readonly churn_type?: "unknown" | "voluntary" | "involuntary" | "mixed" | "not_applicable" | string;
  readonly risk_signal?:
    | "dormant"
    | "no_recent_status_view"
    | "no_document_view"
    | "billing_grace"
    | "cancel_started"
    | "save_attempt"
    | "post_save_monitor"
    | string;
};

type RetentionLifecyclePayload = RetentionCommonPayload & {
  readonly delivery_channel?: "none" | "email" | "in_app" | "push" | "whatsapp" | "manual" | string;
  readonly lifecycle_trigger?:
    | "welcome_due"
    | "postpurchase_checkin_due"
    | "reonboarding_due"
    | "winback_due"
    | "feedback_close_loop_due"
    | "referral_ask_due"
    | "dunning_due"
    | "praise_conversion_due"
    | string;
};

type AgentReadinessBasePayload = {
  readonly agent_engine?: string;
  readonly agent_engine_family?: "chrome" | "other" | string;
  readonly agent_run_id?: string;
  readonly agent_session_class?: "agent" | string;
  readonly agent_surface?: "site" | "calendar" | "posthog" | "crm" | "repo" | "unknown" | string;
  readonly audit_artifact_kind?: "dom" | "screenshot" | "log" | "csv" | "json" | "browser_text" | "unknown" | string;
  readonly audit_artifact_path?: string;
  readonly audit_category?: string;
  readonly audit_confidence?: "high" | "medium" | "low" | string;
  readonly audit_lane?: "00_BLOCKED" | "01_NEEDS_INPUT" | "02_READY_FOR_CONTENT" | "03_READY_FOR_DEV" | "none" | string;
  readonly audit_lens_slug?: string;
  readonly audit_priority?: "P0" | "P1" | "P2" | "P3" | string;
  readonly audit_verdict?: "FINDING" | "PASS" | "N_A" | "N_A_DEPENDENT" | "NOT_DETERMINABLE" | string;
  readonly blocked_reason?: string;
  readonly commercial_impact_band?: "none" | "lead_risk" | "quote_risk" | "booking_risk" | "revenue_risk" | string;
  readonly delegation_surface?: "none" | "calendar" | "form" | "api" | "email" | "crm" | string;
  readonly regression_status?: "not_configured" | "configured_repo" | "configured_live" | "not_applicable" | string;
  readonly source_event_kind?: "browser_event" | "server_event" | "crm_event" | "batch_export" | "manual_import" | string;
  readonly taxonomy_version?: string;
  readonly tracking_surface_state?: "not_defined" | "defined_repo" | "defined_live" | "observed_live" | string;
};

type AgentReadinessTaskStepPayload = AgentReadinessBasePayload & {
  readonly agent_step_id?: string;
  readonly agent_step_result?: "pass" | "fail" | "blocked" | "not_run" | "not_determinable" | string;
  readonly agent_task_id?: string;
  readonly battery_submit_path?: "pre_submit" | "test_payload" | "not_applicable" | string;
  readonly duration_ms?: number;
  readonly route_path?: string;
};

type AgentReadinessAuditSnapshotPayload = AgentReadinessBasePayload & {
  readonly snapshot_check_count?: number;
  readonly snapshot_failed_count?: number;
  readonly snapshot_na_count?: number;
  readonly snapshot_not_determinable_count?: number;
  readonly snapshot_ticket_count?: number;
};

type AgentReadinessOperatorFeedbackPayload = AgentReadinessBasePayload & {
  readonly operator_feedback_state?: "not_requested" | "requested" | "received" | "triaged" | "not_applicable" | string;
};

export type PostHogEventMap = {
  readonly page_not_found: PageNotFoundPayload;
  readonly mailto_link_click: MarketingBasePayload;
  readonly tel_link_click: MarketingBasePayload;
  readonly cta_book_call_click: MarketingBasePayload;
  readonly cta_manual_ai_audit_click: MarketingBasePayload;
  readonly adult_gate_accept: MarketingBasePayload;
  readonly contact_email_click: ContactPayload;
  readonly contact_phone_click: ContactPayload;
  readonly contact_method_click: ContactPayload;
  readonly cta_contact_click: MarketingBasePayload;
  readonly cta_ai_audit_click: MarketingBasePayload;
  readonly lead_magnet_open: MarketingBasePayload;
  readonly lead_magnet_request_click: MarketingBasePayload;
  readonly home_cta_click: MarketingBasePayload;
  readonly founder_proof_click: MarketingBasePayload;
  readonly resource_click: MarketingBasePayload;
  readonly review_proof_click: MarketingBasePayload & { readonly proof_platform?: string };
  readonly review_platform_click: MarketingBasePayload & { readonly proof_platform?: string };
  readonly service_trust_proof_click: MarketingBasePayload;
  readonly app_opened: MarketingBasePayload & { readonly app_surface?: string; readonly display_mode?: string };
  readonly app_installed: MarketingBasePayload & { readonly app_surface?: string; readonly display_mode?: string };
  readonly pwa_install_prompt_available: MarketingBasePayload & { readonly app_surface?: string; readonly display_mode?: string };
  readonly pwa_install_prompt_click: MarketingBasePayload & { readonly app_surface?: string; readonly display_mode?: string };
  readonly pwa_install_prompt_result: MarketingBasePayload & {
    readonly app_surface?: string;
    readonly display_mode?: string;
    readonly install_outcome?: "accepted" | "dismissed" | string;
    readonly prompt_platform?: string;
  };
  readonly pwa_notification_prompt_click: MarketingBasePayload & { readonly app_surface?: string; readonly display_mode?: string };
  readonly pwa_notification_permission_result: MarketingBasePayload & {
    readonly app_surface?: string;
    readonly display_mode?: string;
    readonly permission_state?: NotificationPermission | "unsupported";
  };
  readonly pwa_push_subscription_result: MarketingBasePayload & {
    readonly app_surface?: string;
    readonly display_mode?: string;
    readonly permission_state?: NotificationPermission | "unsupported";
    readonly subscription_status?: "created" | "existing" | "failed" | "not_configured" | "skipped" | "unsupported" | string;
  };
  readonly pricing_band_view: MarketingBasePayload & { readonly pricing_tier?: string };
  readonly pricing_band_cta_click: MarketingBasePayload & { readonly pricing_tier?: string };
  readonly pricing_band_click: MarketingBasePayload & { readonly pricing_tier?: string };
  readonly language_switch_click: MarketingBasePayload & {
    readonly previous_locale?: string;
    readonly target_locale?: string;
  };
  readonly lead_capture_click: MarketingBasePayload;
  readonly audience_path_selected: MarketingBasePayload & {
    readonly audience: string;
    readonly stage?: ScopeStage;
  };
  readonly scope_confidence_bumped: ScopeConfidencePayload;
  readonly scope_confidence_reset: MarketingBasePayload & {
    readonly audience?: string;
    readonly previous_stage?: ScopeStage;
    readonly reset_reason?: string;
    readonly stage?: ScopeStage;
  };
  readonly contact_book_call_click: ContactPayload;
  readonly contact_whatsapp_click: ContactPayload;
  readonly brief_submitted: MarketingBasePayload & {
    readonly audience?: string;
    readonly timeline?: string;
  };
  readonly post_brief_resource_click: MarketingBasePayload & {
    readonly resource_path?: string;
    readonly resource_title?: string;
  };
  readonly onboarding_preview_viewed: MarketingBasePayload & {
    readonly stage?: ScopeStage;
  };
  readonly fit_quiz_started: FitQuizPayload;
  readonly fit_quiz_step_complete: FitQuizPayload;
  readonly fit_quiz_submitted: FitQuizPayload;
  readonly fit_quiz_band_to_cta: FitQuizPayload;
  readonly persona_pdf_download: MarketingBasePayload & {
    readonly audience?: string;
    readonly fit_band?: FitBand;
  };
  readonly roi_calc_used: RoiCalculatorPayload;
  readonly roi_calc_to_book_click: RoiCalculatorPayload;
  readonly partner_badge_impression: PartnerBadgePayload;
  readonly partner_badge_click_through: PartnerBadgePayload;
  readonly partner_application_started: PartnerBadgePayload;
  readonly funnel_planner_started: FunnelPlannerPayload & { readonly from?: string };
  readonly funnel_planner_step_viewed: FunnelPlannerPayload & { readonly step: number; readonly time_on_step_ms?: number };
  readonly funnel_planner_step_completed: FunnelPlannerPayload & { readonly step: number; readonly time_on_step_ms: number };
  readonly funnel_planner_template_changed: FunnelPlannerPayload & { readonly step: number };
  readonly funnel_planner_blueprint_applied: FunnelPlannerBlueprintPayload;
  readonly funnel_planner_audience_selected: FunnelPlannerPayload & { readonly step: number };
  readonly funnel_planner_stage_added: FunnelPlannerPayload & { readonly stage_count: number };
  readonly funnel_planner_stage_removed: FunnelPlannerPayload & { readonly stage_count: number };
  readonly funnel_planner_projection_changed: FunnelPlannerPayload & { readonly step: number };
  readonly funnel_planner_submitted: FunnelPlannerFinalFieldCoveragePayload & { readonly stage_count: number; readonly total_time_ms: number };
  readonly funnel_planner_pdf_downloaded: FunnelPlannerPayload & { readonly duration_ms: number; readonly error_code?: string };
  readonly funnel_planner_abandoned: FunnelPlannerPayload & { readonly step: number };
  readonly funnel_planner_crm_submitted: FunnelPlannerFinalFieldCoveragePayload & { readonly error_code?: string };
  readonly funnel_planner_submit_blocked: FunnelPlannerPayload & { readonly reason?: "honeypot" | string };
  readonly funnel_planner_share_url_opened: FunnelPlannerPayload & { readonly step: number };
  readonly funnel_planner_share_url_created: FunnelPlannerPayload & {
    readonly step: number;
    readonly copied: boolean;
    readonly share_url_type?: "short" | "encoded_fallback";
  };
  readonly funnel_planner_scenario_created: FunnelPlannerScenarioPayload;
  readonly funnel_planner_scenario_compared: FunnelPlannerScenarioPayload;
  readonly funnel_planner_reverse_goal_calculated: FunnelPlannerPayload & {
    readonly goal_type?: "profit" | "revenue" | "leads" | "customers" | "booked_calls" | "cac" | "payback" | string;
    readonly feasible?: boolean;
  };
  readonly funnel_planner_context_menu_action: FunnelPlannerContextMenuPayload;
  readonly funnel_planner_branch_node_added: FunnelPlannerContextMenuPayload;
  readonly funnel_planner_share_stage_link_copied: FunnelPlannerPayload & { readonly copied: boolean };
  readonly funnel_planner_rules_recommendation_viewed: FunnelPlannerRecommendationPayload;
  readonly funnel_planner_rules_recommendation_applied: FunnelPlannerRecommendationPayload;
  readonly funnel_planner_export_downloaded: FunnelPlannerExportPayload;
  readonly funnel_planner_model_health_warning_viewed: FunnelPlannerModelHealthPayload;
  // NL-041/010/014 advisory panels. These are session-only advisory surfaces:
  // payloads carry only categorical/enum state, never a raw price, visitor
  // volume, revenue figure, or any other computed estimate value.
  readonly funnel_planner_advisory_package_decided: FunnelPlannerPayload & {
    readonly decision: "accepted" | "overridden" | "skipped";
    readonly selected_tier?: string;
  };
  readonly funnel_planner_advisory_seasonal_changed: FunnelPlannerPayload & {
    readonly readiness: "disabled" | "ready" | "blocked";
    readonly enabled: boolean;
  };
  readonly funnel_planner_advisory_explanation_viewed: FunnelPlannerPayload & {
    readonly has_receipt: boolean;
  };
  readonly experience_variant_exposed: ExperienceEngagementPayload;
  readonly experience_zone_viewed: ExperienceEngagementPayload;
  readonly experience_zone_engaged: ExperienceEngagementPayload;
  readonly parallax_variant_viewed: ExperienceEngagementPayload;
  readonly parallax_variant_engaged: ExperienceEngagementPayload;
  readonly cta_impression: ExperienceEngagementPayload;
  readonly cta_engaged: ExperienceEngagementPayload;
  readonly component_impression: ExperienceEngagementPayload;
  readonly component_engaged: ExperienceEngagementPayload;
  readonly portal_activation_step: PortalActivationPayload;
  readonly portal_adoption_signal: PortalAdoptionPayload;
  readonly retention_feedback_signal: RetentionFeedbackPayload;
  readonly retention_risk_signal: RetentionRiskPayload;
  readonly retention_lifecycle_trigger: RetentionLifecyclePayload;
  readonly agent_readiness_task_step: AgentReadinessTaskStepPayload;
  readonly agent_readiness_audit_snapshot: AgentReadinessAuditSnapshotPayload;
  readonly agent_readiness_operator_feedback: AgentReadinessOperatorFeedbackPayload;
  readonly webmcp_action_result: {
    readonly webmcp_action: string;
    readonly webmcp_risk: "R0" | "R1" | "R2" | "R3";
    readonly webmcp_outcome: string;
    readonly webmcp_duration_bucket: "lt_1s" | "1_to_10s" | "over_10s";
  };
  readonly whatsapp_aggregate_message_daily: WhatsAppAggregateAnalyticsPayload;
  readonly whatsapp_aggregate_flow_funnel: WhatsAppAggregateAnalyticsPayload;
  readonly whatsapp_aggregate_campaign_outcome: WhatsAppAggregateAnalyticsPayload;
};

export type PostHogEventName = keyof PostHogEventMap;

export const GAMIFICATION_POSTHOG_EVENT_NAMES = [
  "audience_path_selected",
  "scope_confidence_bumped",
  "scope_confidence_reset",
  "contact_book_call_click",
  "contact_email_click",
  "contact_whatsapp_click",
  "brief_submitted",
  "post_brief_resource_click",
  "onboarding_preview_viewed",
  "fit_quiz_started",
  "fit_quiz_step_complete",
  "fit_quiz_submitted",
  "fit_quiz_band_to_cta",
  "persona_pdf_download",
  "roi_calc_used",
  "roi_calc_to_book_click",
  "partner_badge_impression",
  "partner_badge_click_through",
  "partner_application_started",
] as const satisfies readonly PostHogEventName[];

export const FUNNEL_PLANNER_POSTHOG_EVENT_NAMES = [
  "funnel_planner_started",
  "funnel_planner_step_viewed",
  "funnel_planner_step_completed",
  "funnel_planner_template_changed",
  "funnel_planner_blueprint_applied",
  "funnel_planner_audience_selected",
  "funnel_planner_stage_added",
  "funnel_planner_stage_removed",
  "funnel_planner_projection_changed",
  "funnel_planner_submitted",
  "funnel_planner_pdf_downloaded",
  "funnel_planner_abandoned",
  "funnel_planner_crm_submitted",
  "funnel_planner_submit_blocked",
  "funnel_planner_share_url_opened",
  "funnel_planner_share_url_created",
  "funnel_planner_scenario_created",
  "funnel_planner_scenario_compared",
  "funnel_planner_reverse_goal_calculated",
  "funnel_planner_context_menu_action",
  "funnel_planner_branch_node_added",
  "funnel_planner_share_stage_link_copied",
  "funnel_planner_rules_recommendation_viewed",
  "funnel_planner_rules_recommendation_applied",
  "funnel_planner_export_downloaded",
  "funnel_planner_model_health_warning_viewed",
  "funnel_planner_advisory_package_decided",
  "funnel_planner_advisory_seasonal_changed",
  "funnel_planner_advisory_explanation_viewed",
] as const satisfies readonly PostHogEventName[];

export const EXPERIENCE_ENGAGEMENT_POSTHOG_EVENT_NAMES = [
  "experience_variant_exposed",
  "experience_zone_viewed",
  "experience_zone_engaged",
  "parallax_variant_viewed",
  "parallax_variant_engaged",
  "cta_impression",
  "cta_engaged",
  "component_impression",
  "component_engaged",
] as const satisfies readonly PostHogEventName[];

export const RETENTION_POSTHOG_EVENT_NAMES = [
  "portal_activation_step",
  "portal_adoption_signal",
  "retention_feedback_signal",
  "retention_risk_signal",
  "retention_lifecycle_trigger",
] as const satisfies readonly PostHogEventName[];

export const AGENT_READINESS_POSTHOG_EVENT_NAMES = [
  "agent_readiness_task_step",
  "agent_readiness_audit_snapshot",
  "agent_readiness_operator_feedback",
] as const satisfies readonly PostHogEventName[];

export const WEBMCP_POSTHOG_EVENT_NAMES = ["webmcp_action_result"] as const satisfies readonly PostHogEventName[];

export const WHATSAPP_AGGREGATE_POSTHOG_EVENT_NAMES = [
  "whatsapp_aggregate_message_daily",
  "whatsapp_aggregate_flow_funnel",
  "whatsapp_aggregate_campaign_outcome",
] as const satisfies readonly PostHogEventName[];

export const POSTHOG_EVENT_NAMES: ReadonlySet<PostHogEventName> = new Set([
  "page_not_found",
  "mailto_link_click",
  "tel_link_click",
  "cta_book_call_click",
  "cta_manual_ai_audit_click",
  "adult_gate_accept",
  "contact_email_click",
  "contact_phone_click",
  "contact_method_click",
  "cta_contact_click",
  "cta_ai_audit_click",
  "lead_magnet_open",
  "lead_magnet_request_click",
  "home_cta_click",
  "founder_proof_click",
  "resource_click",
  "review_proof_click",
  "review_platform_click",
  "service_trust_proof_click",
  "app_opened",
  "app_installed",
  "pwa_install_prompt_available",
  "pwa_install_prompt_click",
  "pwa_install_prompt_result",
  "pwa_notification_prompt_click",
  "pwa_notification_permission_result",
  "pwa_push_subscription_result",
  "pricing_band_view",
  "pricing_band_cta_click",
  "pricing_band_click",
  "language_switch_click",
  "lead_capture_click",
  ...GAMIFICATION_POSTHOG_EVENT_NAMES,
  ...FUNNEL_PLANNER_POSTHOG_EVENT_NAMES,
  ...EXPERIENCE_ENGAGEMENT_POSTHOG_EVENT_NAMES,
  ...RETENTION_POSTHOG_EVENT_NAMES,
  ...AGENT_READINESS_POSTHOG_EVENT_NAMES,
  ...WEBMCP_POSTHOG_EVENT_NAMES,
  ...WHATSAPP_AGGREGATE_POSTHOG_EVENT_NAMES,
]);

export const POSTHOG_EVENT_PAYLOAD_KEYS: ReadonlySet<string> = new Set([
  "webmcp_action", "webmcp_risk", "webmcp_outcome", "webmcp_duration_bucket",
  "aggregation_schema",
  "window_from",
  "window_to",
  "date",
  "inbound",
  "outbound",
  "sent",
  "delivered",
  "read",
  "failed",
  "reply_rate_percent",
  "flow_version_id",
  "node_id",
  "action_type",
  "attempts",
  "successful",
  "blocked",
  "drop_off_percent",
  "campaign_id",
  "total_jobs",
  "scheduled",
  "completed",
  "cancelled",
  "completion_rate_percent",
  "agent_engine",
  "agent_engine_family",
  "agent_run_id",
  "agent_session_class",
  "agent_step_id",
  "agent_step_result",
  "agent_surface",
  "agent_task_id",
  "app_surface",
  "audience",
  "audit_artifact_kind",
  "audit_artifact_path",
  "audit_category",
  "audit_confidence",
  "audit_lane",
  "audit_lens_slug",
  "audit_priority",
  "audit_verdict",
  "aov_band",
  "action",
  "activation_step",
  "adoption_signal",
  "battery_submit_path",
  "blocked_reason",
  "buyer_segment",
  "budget_stage_bucket",
  "campaign_key",
  "commercial_impact_band",
  "component_family",
  "component_id",
  "contact_method",
  "content_theme",
  "conversion_rate_band",
  "cta_id",
  "cta_location",
  "device_shell",
  "delegation_surface",
  "display_mode",
  "event_transport",
  "edge_kind",
  "experiment_key",
  "experiment_variant",
  "experience_id",
  "fit_band",
  "feedback_signal",
  "feedback_stage",
  "feasible",
  "from",
  "goal_type",
  "global_holdout",
  "holdout_key",
  "install_outcome",
  "intended_path",
  "locale",
  "locale_direction",
  "lifecycle_trigger",
  "marketing_compliance_gate",
  "motion_mode",
  "node_kind",
  "operator_feedback_state",
  "partner_visibility",
  "persona",
  "permission_state",
  "portal_state",
  "previous_locale",
  "previous_stage",
  "pricing_tier",
  "projected_revenue_band",
  "projection_months",
  "prompt_platform",
  "proof_platform",
  "quiz_answer_id",
  "quiz_step",
  "reset_reason",
  "recommended_path",
  "recommended_service_line",
  "referrer_bucket",
  "recommendation_severity",
  "recommendation_source",
  "reason",
  "result_tier",
  "retention_stage",
  "retention_policy_version",
  "risk_signal",
  "regression_status",
  "resource_path",
  "resource_title",
  "route_path",
  "route_template_id",
  "sampling_tier",
  "service_path",
  "service_line",
  "shell_detection_state",
  "sentiment_bucket",
  "signal_count_bucket",
  "snapshot_check_count",
  "snapshot_failed_count",
  "snapshot_na_count",
  "snapshot_not_determinable_count",
  "snapshot_ticket_count",
  "source",
  "source_cta",
  "source_event_kind",
  "source_locale",
  "source_path",
  "source_template",
  "scenario_kind",
  "stage",
  "subscription_status",
  "target_locale",
  "taxonomy_version",
  "timeline",
  "tool_key",
  "tracking_surface_state",
  "traffic_band",
  "variant_key",
  "vertical_18plus",
  "vertical_18plus_reason",
  "xp_depth",
  "xp_zone",
  "churn_type",
  "delivery_channel",
  "duration_ms",
  "error_code",
  "stage_count",
  "planner_tracking_schema_version",
  "planner_state_version",
  "crm_snapshot_schema_version",
  "final_field_count",
  "field_group_count",
  "traffic_channel_count",
  "product_count",
  "graph_node_count",
  "graph_edge_count",
  "expense_count",
  "scenario_count",
  "experiment_count",
  "actual_row_count",
  "asset_count",
  "tracking_configured_count",
  "has_graph",
  "has_actuals",
  "has_expenses",
  "has_assets",
  "has_capacity_cap",
  "has_custom_scenarios",
  "has_experiments",
  "has_reinvestment",
  "has_model_health_warnings",
  "model_health_warning_count",
  "model_health_warning_category_count",
  "visitor_band",
  "customer_band",
  "monthly_revenue_band",
  "monthly_expense_band",
  "warning_categories",
  "warning_count",
  "step",
  "template_id",
  "time_on_step_ms",
  "total_time_ms",
]);

declare global {
  interface Window {
    __seohPostHogCaptureComponentEngagement?: (payload: Record<string, unknown>) => boolean;
    __seohPostHogTrackEvent?: (eventName: string, payload?: Record<string, unknown>) => boolean;
    __seohTrackEvent?: (eventName: string, payload?: Record<string, unknown>) => boolean;
  }
}

export function isPostHogEventName(eventName: string): eventName is PostHogEventName {
  return POSTHOG_EVENT_NAMES.has(eventName as PostHogEventName);
}

export function track<K extends PostHogEventName>(eventName: K, payload: PostHogEventMap[K]): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const eventPayload = payload as Record<string, unknown>;

  if (typeof window.__seohTrackEvent === "function") {
    const tracked = window.__seohTrackEvent(eventName, eventPayload) === true;

    if (tracked) {
      return true;
    }
  }

  return window.__seohPostHogTrackEvent?.(eventName, eventPayload) === true;
}

export function trackPostHog<K extends PostHogEventName>(eventName: K, payload: PostHogEventMap[K]): boolean {
  if (typeof window === "undefined" || typeof window.__seohPostHogTrackEvent !== "function") {
    return false;
  }

  // Use the canonical PostHog bridge exactly once. The generic `track()` path may
  // also traverse the legacy multi-transport bridge, which already delegates to
  // PostHog for its own allow-listed events.
  return window.__seohPostHogTrackEvent(eventName, payload as Record<string, unknown>) === true;
}
